// Public site data — real content only. No placeholders visible to visitors.

export const HERO = {
  eyebrow: "Workplace Chaplaincy · Greater Sacramento",
  title: "Taking Care of the People Who Take Care of Your Business.",
  subtitle:
    "Confidential workplace chaplaincy and employee support for businesses throughout Greater Sacramento, Placer, El Dorado, and Nevada Counties.",
  nonClinicalNote: "Non-clinical. Voluntary. Respectful of every belief and background.",
  ctaPrimary: {
    label: "Schedule a 15-Minute Business Consultation",
    to: "/schedule?type=organizational_consultation&duration=15",
  },
  ctaSecondary: { label: "Explore Employee Support Services", to: "/services/workplace-chaplaincy" },
  image:
    "https://images.unsplash.com/photo-1758691737278-3af15b37af48?fm=jpg&w=2000&q=85&fit=max",
  imageAlt: "Two colleagues in conversation in a calm modern office",
};

export const INDUSTRIES = [
  "Technology",
  "Healthcare",
  "Manufacturing",
  "Logistics",
  "Hospitality",
  "Public Sector",
  "Nonprofit",
];

export const STATS = [
  { value: "24/7", label: "on-call chaplain access — every organization, every plan" },
  { value: "100%", label: "confidential employee conversations" },
  { value: "3-in-1", label: "scheduled on-site, remote, or blended models" },
  { value: "0", label: "religious agenda — care for every background" },
];

export const WORKPLACE_REALITIES = [
  { title: "Employee stress", body: "Daily pressure, deadlines, and uncertainty add up — quietly." },
  { title: "Burnout", body: "Sustained exhaustion that performance reviews and PTO alone can't resolve." },
  { title: "Grief & loss", body: "Personal loss, illness, and life transitions follow employees to work." },
  { title: "Workplace conflict", body: "Tension between teammates, teams, or with leadership." },
  { title: "Crisis response", body: "Critical incidents, sudden losses, or workplace emergencies — met with rapid, confidential, non-clinical support shaped by law enforcement crisis-response experience." },
  { title: "Leadership fatigue", body: "Executives and managers carrying the weight without a confidential outlet." },
  { title: "Emotional wellness", body: "Quiet, non-clinical conversations that help employees feel seen." },
];

export const HOW_IT_WORKS = [
  {
    title: "Employees access confidential support",
    body: "Through the Care Portal, on-site visits, or scheduled remote sessions — privately, without managers, without paperwork.",
  },
  {
    title: "Chaplains provide non-clinical care",
    body: "Trusted, trained chaplains offer presence, listening, and emotional and workplace support — never therapy, never religious pressure.",
  },
  {
    title: "Organizations receive workforce care",
    body: "You receive ongoing relationship-based support for your people, plus optional aggregate usage insights — no individual details.",
  },
];

export const EAP_COMPARISON = [
  ["When chaplains are reachable", "24/7 on-call access — phone, text, video (scheduled), and confidential message", "Varies — often business hours plus a phone-based crisis line"],
  ["Type of care", "Non-clinical, relational presence", "Short-term clinical counseling and referrals"],
  ["Relationship", "Same chaplain, ongoing relationship", "Different counselor each call"],
  ["Confidentiality", "Yes — aggregate org reporting only", "Yes — but session count is often visible"],
  ["Where it happens", "Scheduled on-site, remote, or blended", "Phone, video, sometimes in-person"],
  ["Frequency", "Continuous presence — weekly, biweekly, on-demand", "Episodic — typically a fixed session cap per issue"],
  ["Crisis response", "Same-day on-site or remote deployment", "Phone-based crisis line"],
  ["Leadership support", "Confidential 1:1s for executives and managers", "Same generic clinical pathway as employees"],
  ["Religious tone", "Neutral — care for every background, no agenda", "Neutral"],
  ["Best for", "Daily relational care and culture", "Acute clinical and benefits-style needs"],
];

export const FAQS = [
  {
    q: "When are chaplains available?",
    a: "CCCHAPS chaplain support is available 24/7 on-call — standard for every organization regardless of package or contract term. Depending on how CALLACHAP is configured for their organization, employees may reach a chaplain through phone, text, confidential message, and available remote-support channels. Scheduled on-site visits happen during agreed-upon windows. Confidential messages receive follow-up; they are not a live-chat channel, and response times may vary by channel. In a life-threatening emergency, employees should call 911.",
  },
  {
    q: "Is this confidential?",
    a: "Yes. Conversations between an employee and a CCCHAPS chaplain are handled with confidentiality and respect for appropriate professional, ethical, safety, and legal limits. Employers receive aggregate usage information only — never individual names, topics, or details.",
  },
  {
    q: "Is this therapy?",
    a: "No. Chaplaincy is non-clinical, relational support — presence, listening, and human care. When professional counseling or medical help is needed, chaplains help employees connect to the right resource.",
  },
  {
    q: "How is this different from an EAP?",
    a: "An EAP typically provides a fixed number of short-term clinical counseling sessions with a rotating pool of counselors. CCCHAPS provides an ongoing, relational presence with the same chaplain over time — through scheduled on-site, remote, or blended support shaped around the organization's needs and locations.",
  },
  {
    q: "Is participation voluntary?",
    a: "Always. Employees choose whether, when, and how to engage. Managers do not see who has spoken with a chaplain.",
  },
  {
    q: "Can employees access support virtually?",
    a: "Yes. Depending on the organization's care arrangement, employees may access chaplain support through configured phone, text, confidential-message, and available remote-support channels. Scheduled on-site and blended support may also be available based on the organization's needs and locations.",
  },
];

export const SERVICES = [
  {
    slug: "workplace-chaplaincy",
    title: "Workplace Chaplaincy",
    tagline: "24/7 on-call chaplain support for your workforce, with scheduled on-site, remote, or blended coverage.",
    icon: "Users",
    summary:
      "Trained chaplains present in the flow of work — building trusted relationships and providing confidential, human support alongside existing employee benefits.",
    bullets: [
      "24/7 on-call chaplain access — phone, text, video, and confidential message",
      "Regular workplace presence — scheduled on-site, remote, or blended",
      "Confidential 1:1 conversations",
      "Relationship-based engagement across our service area",
      "Same chaplain, ongoing continuity",
    ],
    body:
      "Workplace chaplaincy is a relational service, not a clinical one. CCCHAPS chaplains walk the floor, share coffee, remember names, and become a steady, trusted presence over months and years. Employees can talk to their chaplain about anything — stress, family, faith, no faith, a hard week, or nothing in particular. Managers never receive individual details. Coverage is available scheduled on-site, remote, or blended to match how your teams actually operate.",
    cta: "Request a Consultation",
  },
  {
    slug: "healthcare-hospice",
    title: "Workplace Chaplain Care for Healthcare Teams",
    cardTitle: "Healthcare",
    cardSummary:
      "Confidential, non-clinical chaplain support for healthcare professionals, hospice and palliative care teams, patients, families, support staff, and leadership — providing relational care through demanding and emotionally difficult seasons.",
    metaTitle:
      "Healthcare Workplace Chaplaincy — Hospitals, Hospice, Home Health & Clinics | California Corporate Chaplains",
    metaDescription:
      "Confidential, non-clinical workplace chaplaincy for healthcare employees, hospice and palliative-care teams, home health, clinics, rehabilitation, and multi-site healthcare organizations. 24/7 on-call chaplain access included.",
    tagline:
      "Confidential, non-clinical, relationship-based chaplain support for the people who care for others across healthcare and hospice environments.",
    icon: "HeartPulse",
    summary:
      "Non-clinical emotional and spiritual support for healthcare professionals, hospice and palliative-care teams, patients, families, and leadership — respectful of every belief and preference.",
    bullets: [
      "24/7 on-call chaplain availability for healthcare employees and their immediate household members",
      "Scheduled on-site, remote, or blended chaplain presence based on your Care Team Package",
      "CALLACHAP, CCCHAPS Care Portal, and CCCHAPS On Demand included",
      "Non-clinical support for compassion fatigue, grief, and moral and emotional strain",
      "Ministry of presence with patients and families where the arrangement includes it",
      "Grief, bereavement, and end-of-life presence within professional chaplain scope",
      "Aggregate, non-identifying organizational reporting",
      "Leadership consultation where appropriate",
      "Voluntary — always the individual's choice; respectful of belief and non-belief",
    ],
    settings: [
      "Hospitals",
      "Hospice & Palliative Care",
      "Home Health",
      "Outpatient Clinics",
      "Rehabilitation",
      "Behavioral Health organizations (within non-clinical chaplain scope)",
      "Healthcare practices and multi-site healthcare organizations",
    ],
    body:
      "Healthcare professionals show up for people on their hardest days. They walk with patients through diagnosis, treatment, recovery, decline, and loss — often while carrying their own grief, family concerns, financial stress, caregiving responsibilities, and everyday life at the same time. CCCHAPS chaplains provide a steady, confidential person outside the clinical and reporting lines that healthcare teams can reach when they need someone to talk to.\n\nCCCHAPS has particular capability in grief, serious illness, caregiving, and end-of-life concerns. Our chaplains meet nurses, physicians, hospice and palliative-care teams, home-health clinicians, techs, aides, and support staff in the rhythm of their work — during a shift, after a hard case, or when something at home has become too heavy to carry through another day.\n\nHospice and palliative care remain a core part of what CCCHAPS supports within Healthcare. That includes presence with clinicians and support staff who are exposed to loss regularly, and — where the organization's service arrangement includes it — companionship and non-clinical support with patients and families through decline, end-of-life, and after loss.\n\nCCCHAPS complements — it does not replace — HR, EAP, medical or mental-health treatment, clinical social work, hospital staff chaplains, hospice spiritual-care coordinators, internal pastoral or spiritual-care departments, an employee's own clergy or faith community, or emergency services. Chaplain support is voluntary, permission-based, respectful of belief and non-belief, and confidential within appropriate professional and legal limits.\n\nEvery CCCHAPS service arrangement includes 24/7 on-call chaplain access, CALLACHAP, the CCCHAPS Care Portal, and CCCHAPS On Demand. Scheduled on-site, remote, or blended chaplain presence is built around your Care Team Package and the pace of the environment — a hospital unit, a hospice team meeting, a home-health region, a clinic, or a multi-site healthcare organization.",
    cta: "Request a Healthcare Consultation",
  },
  {
    slug: "senior-living",
    title: "Workplace & Resident Chaplain Care for Senior Living & Board and Care Communities",
    heroImage: {
      src: "/images/senior-living/hero.jpg",
      alt: "A caregiver in a warm, unhurried conversation with an older adult resident at a home dining table.",
    },
    audienceImage: {
      src: "/images/senior-living/companion.jpg",
      alt: "Two older adults seated together outdoors in a calm, dignified conversation.",
    },
    cardTitle: "Senior Living / Board & Care",
    cardSummary:
      "Relationship-based chaplain care for senior living and board & care communities — supporting caregivers and staff, offering permission-based care to residents, and walking alongside families through change, decline, grief, and difficult transitions.",
    metaTitle:
      "Senior Living & Board and Care Chaplaincy — Staff, Residents & Families | California Corporate Chaplains",
    metaDescription:
      "Relationship-based chaplain care for senior living and board & care — assisted living, memory care, skilled nursing, and residential care. Confidential support for caregivers and staff, permission-based care for residents, and steady presence with families.",
    tagline:
      "Confidential chaplain support for staff, permission-based care for residents, and steady presence with families through change, decline, grief, and difficult transitions.",
    icon: "Home",
    summary:
      "Relationship-based, non-clinical chaplain care shaped around the realities of senior living and board & care — staff, residents, and families each supported within appropriate chaplain scope.",
    bullets: [
      "Scheduled on-site chaplain presence based on your Care Team Package",
      "24/7 on-call chaplain access for eligible employees and their immediate household members",
      "CALLACHAP, CCCHAPS Care Portal, and CCCHAPS On Demand for employees where included",
      "Permission-based relational, emotional, and spiritual support for residents",
      "Support for families through serious illness, decline, caregiving, and loss",
      "Presence during resident deaths, difficult transitions, and community grief",
      "Leadership consultation where appropriate",
      "Aggregate, non-identifying organizational reporting",
      "Voluntary — respectful of belief and non-belief for staff, residents, and families",
    ],
    settings: [
      "Independent Living",
      "Assisted Living",
      "Memory Care",
      "Skilled Nursing / Long-Term Care",
      "Continuing Care Retirement Communities (where applicable)",
      "Board & Care / Residential Care",
      "Multi-site senior-living organizations",
    ],
    audiences: [
      {
        label: "Staff",
        body:
          "Confidential chaplain support for caregivers, nursing staff, dining and hospitality teams, maintenance, activities, administrative staff, and leadership — for personal concerns, grief, caregiving pressure, family and relationship issues, workplace stress, serious illness, spiritual questions, and other appropriate life challenges. Employees receive 24/7 on-call chaplain access, CALLACHAP, the CCCHAPS Care Portal, and CCCHAPS On Demand as part of the organization's CCCHAPS service arrangement.",
      },
      {
        label: "Residents",
        body:
          "Permission-based relational, emotional, and spiritual support — conversation, companionship, encouragement, ministry of presence, prayer when desired, and spiritual support when desired. Chaplain visits may include grief and loss support, presence during serious illness or decline, and support through loneliness or major transitions. Residents do not need to be religious to receive chaplain support; belief and non-belief are equally respected, and engagement is always the resident's choice.",
      },
      {
        label: "Families",
        body:
          "Support for families navigating serious illness, decline, caregiving transitions, anticipatory grief, death and loss, difficult family transitions, and emotional or spiritual concerns. All family support is voluntary, permission-based, non-clinical, and within professional chaplain scope.",
      },
    ],
    body:
      "Senior living and board & care communities bring together the everyday responsibility of caring for people — residents who are aging, families walking with them through change, and the staff who show up shift after shift. CCCHAPS chaplains provide a steady, familiar presence in those communities — someone residents come to know, someone families can lean on during hard transitions, and someone staff can reach when they need to talk without it becoming a clinical or HR conversation.\n\nThe intent is not to replace what a community already provides. CCCHAPS complements facility clinical services, medical care, mental-health treatment, social work, hospice services, facility staff chaplains, residents' existing clergy or faith communities, and emergency services. Our chaplains are trained to know their scope and to help staff, residents, or families connect with the right professional when a situation calls for one.\n\nCare is intentionally differentiated across three populations. Staff receive confidential chaplain support around the personal and professional realities of caregiving work. Residents may receive permission-based relational, emotional, and spiritual support on their own terms. Families may receive support through the seasons of change that come with serious illness, decline, and loss.\n\nEvery arrangement is built around a CCCHAPS Care Team Package sized to the community — scheduled on-site chaplain presence at a cadence that fits the operation, 24/7 on-call chaplain access for eligible employees and their immediate household members, and CALLACHAP, the CCCHAPS Care Portal, and CCCHAPS On Demand as included baseline components of the organization's service arrangement. Board & Care and Residential Care are intentional CCCHAPS markets — care can be scaled up or down to fit smaller residential settings just as thoughtfully as larger senior-living communities.",
    cta: "Discuss Chaplain Care for Your Community",
  },
  {
    slug: "crisis-grief-bereavement",
    title: "Crisis, Grief & Bereavement Support",
    heroImage: {
      src: "/images/crisis-grief-bereavement/hero.jpg",
      alt: "Two professionals seated at a small table by a window in a calm, supportive one-on-one conversation.",
    },
    tagline:
      "Every CCCHAPS care package includes 24/7 on-call chaplain access for crisis, grief, and difficult workplace events. Organizations may also contact CCCHAPS when immediate crisis or grief support is needed.",
    icon: "ShieldAlert",
    summary:
      "Confidential, non-clinical chaplain support for sudden loss, workplace incidents, and hard news — with ongoing grief and bereavement care as an included part of every CCCHAPS care arrangement.",
    bullets: [
      "24/7 on-call chaplain access",
      "On-site or remote chaplain response when available and appropriate",
      "Leadership and HR support during difficult workplace events",
      "Ongoing grief and bereavement support for employees, families, and teams",
    ],
    body:
      "When something serious happens — the death of an employee or family member, a sudden diagnosis, a difficult workplace incident, or other hard news — people often need more than information. They need a calm, trusted presence.\n\nCCCHAPS chaplains are available 24/7 on-call to support employees, leaders, and teams during difficult moments. Depending on the circumstances and the organization's care arrangement, support may begin by phone, text, confidential message, remote connection, or an appropriate on-site response.\n\nOur role is not to take over the responsibilities of leadership, HR, emergency responders, medical professionals, or mental-health providers. We provide confidential, non-clinical chaplain support and help people navigate the human impact of what has happened.\n\nSupport does not necessarily end when the immediate event is over. Grief and adjustment often continue long after the first difficult day. CCCHAPS can remain present through appropriate follow-up, helping employees, families, and teams move through loss and change with dignity and support.",
    clientCallouts: [
      {
        label: "Already a CCCHAPS Client?",
        body: "24/7 on-call chaplain access for crisis, grief, and difficult workplace events is already included in every CCCHAPS care package.",
      },
      {
        label: "Not Yet a Client?",
        body: "Organizations may still contact CCCHAPS when immediate crisis or grief support is needed. We can discuss the situation and determine the appropriate next step.",
      },
    ],
    ctaLead: {
      title: "Need Crisis or Grief Support?",
      body: "Whether your organization is already a CCCHAPS client or is reaching out for the first time, we are available to discuss the situation and determine the appropriate next step.",
    },
    safetyNote:
      "CCCHAPS provides non-clinical chaplain support and is not an emergency medical, psychiatric, or crisis-response service. Life-threatening emergencies should be directed to 911 or the appropriate emergency resource.",
    cta: "Request Crisis or Grief Support",
  },
  {
    slug: "leadership-employee-support",
    title: "Leadership / Employee Support",
    tagline: "Confidential 1:1 support for executives, managers, and every employee.",
    icon: "Compass",
    summary:
      "A confidential sounding board for leaders under pressure, plus everyday relational care for the whole workforce.",
    bullets: [
      "24/7 on-call chaplain access — phone, text, video, and confidential message",
      "Executive and founder 1:1 sessions",
      "Manager support and decision-fatigue care",
      "Employee 1:1s across roles and shifts",
      "No reporting line, no performance implications",
    ],
    body:
      "Leaders are expected to be steady for everyone else. That cost adds up, and there are conversations they can't have with their team or their board. CCCHAPS chaplains provide confidential 1:1 support to executives, managers, and every employee — no reporting line, no performance review, no clinical documentation. Just space to think out loud with someone trained and trusted.",
    cta: "Request Leadership or Employee Support",
  },
  {
    slug: "training-workshops",
    title: "Training & Workshops",
    tagline: "Organizational education and workforce support led by trained chaplains.",
    icon: "GraduationCap",
    summary:
      "Customized workshops for managers, HR teams, and full-workforce sessions — focused on people, resilience, and hard seasons at work.",
    bullets: [
      "Employee stress and resilience",
      "Preventing and responding to burnout",
      "Grief and loss in the workplace",
      "Supporting employees through serious illness or family crisis",
      "Compassionate communication",
      "Workplace crisis and critical-incident support",
      "Leadership support and manager toolkits",
      "Caregiver stress",
      "Creating a culture where employees feel seen, heard, and valued",
    ],
    body:
      "CCCHAPS training and workshops are organizational education and workforce support — not religious programming. Every session is designed for a mixed workforce, focused on the practical human realities of modern work: pressure, transitions, loss, conflict, and how leaders and teams support one another through them.\n\nWorkshops are customized to your organization's context, industry, and team dynamics. They can stand alone or complement ongoing chaplaincy services, giving managers and employees language and tools for the hardest parts of leading and caring for people at work.",
    cta: "Request a Training or Workshop Consultation",
  },
];

export const SERVICE_BY_SLUG = Object.fromEntries(SERVICES.map((s) => [s.slug, s]));

// Books & Resources — real titles, real Amazon links, real author. No placeholders.
export const BOOKS = [
  {
    slug: "from-diagnosis-to-goodbye",
    title: "From Diagnosis to Goodbye",
    subtitle: "Restoring the Patient's Voice Through Every Stage of the Journey",
    author: "Derrick Hall",
    audience:
      "A professional resource offered through California Corporate Chaplains for hospice organizations, healthcare professionals, caregivers, patients and families, grief and bereavement support, employee-support situations involving serious illness or loss, senior-care organizations, and community and organizational education.",
    description:
      "From Diagnosis to Goodbye is written for professionals and families walking alongside someone through a life-altering diagnosis. It centers the patient's voice — their choices, their meaning, their story — through every stage of the journey. The book is non-denominational and appropriate for readers of any background or belief. It is regularly used by hospice teams, healthcare organizations, and grief support groups as a training and companion resource.",
    audiencesGrid: [
      "Hospice organizations",
      "Healthcare professionals",
      "Caregivers",
      "Patients & families",
      "Grief & bereavement support",
      "Employee-support situations",
      "Senior-care organizations",
      "Community & organizational education",
    ],
    coverImage: "/images/books/from-diagnosis-to-goodbye.jpg",
    coverAlt: "Front cover of From Diagnosis to Goodbye — Restoring the Patient's Voice Through Every Stage of the Journey by Chaplain Derrick Hall.",
    amazonUrl: "https://www.amazon.com/dp/0980212723",
  },
];

export const WORKBOOKS = [
  {
    slug: "hospice-workbook",
    title: "From Diagnosis to Goodbye — Hospice Workbook",
    subtitle: "A Guided Companion for Preparation, Presence, and Family Conversation",
    status: "AVAILABLE NOW",
    amazonUrl: "https://www.amazon.com/dp/0980212731",
    body:
      "A guided companion for hospice teams, patients, caregivers, and families — designed for preparation, presence, and honest family conversation through the hospice journey.",
  },
  {
    slug: "bereavement-workbook",
    title: "From Diagnosis to Goodbye — Bereavement Workbook",
    subtitle: "A Guided Companion for Reflection and Conversation",
    status: "AVAILABLE NOW",
    amazonUrl: "https://www.amazon.com/dp/098021274X",
    body:
      "A guided companion for individuals and small groups navigating bereavement — a structured space for reflection, remembrance, and conversation with those walking alongside.",
  },
];

export const CONTACT = {
  email: "office@ccchaps.com",
  phone: "(279) 400-9780",
  phoneHref: "tel:+12794009780",
  businessHours: "Monday–Friday, 9:00 AM–5:00 PM",
  afterHoursPhone: "(916) 755-8221",
  afterHoursPhoneHref: "tel:+19167558221",
  responseCommitment: "We respond within one business day.",
  serviceArea: "Serving Sacramento, Placer, El Dorado & Nevada Counties in person • Remote support available across California and nationwide",
  serviceAreaOnsite: "Onsite: Sacramento, Placer, El Dorado & Nevada Counties",
  serviceAreaRemote: "Remote: California & nationwide",
  serviceAreaLong:
    "CCCHAPS currently provides scheduled onsite chaplaincy services throughout Sacramento, Placer, El Dorado, and Nevada Counties. Remote chaplain support is available across California and nationwide, based on service arrangement and availability.",
};
